/**
 * Putting a car on the draft, and taking one off — a 1:1 port of Android's `OrderDraftCar.kt` and
 * `OrderDraftExtraCars.kt`.
 *
 * These are not simple field assignments, which is why they live apart from the wizard's generic
 * `update`. Each one enforces a rule the backend also enforces, and getting them wrong does not
 * produce a wrong screen — it produces an order the server refuses on the payment step, several
 * taps away from the control that would have fixed it.
 */
import { ORDER_STEPS } from './stateMachine';
import type { ExtraCar, OrderDraft, OrderStep } from './draft';
import { canAddAnotherCar, freshDraftKey } from './draft';

/** The car fields the draft denormalizes, in one holder rather than five positional strings. */
export interface DraftCar {
  id: string;
  /** «Toyota Camry · А123ВС77» — built where the car itself is, not reassembled later. */
  label: string;
  /** Separate from `label` because the first word of it is not reliably the brand. */
  brand: string;
  fuelTypeCode: string;
  tankVolumeLiters: string | null;
}

/** Never forward. Used to walk the wizard *back* when a swap invalidates what came after. */
function earlierOf(a: OrderStep, b: OrderStep): OrderStep {
  return ORDER_STEPS.indexOf(a) <= ORDER_STEPS.indexOf(b) ? a : b;
}

/**
 * Chooses the primary car — first time, mid-flow swap, or seeding a repeat.
 *
 * **Everything else the person filled in survives.** Address, presence, time, payment, comment and
 * the idempotency key all stay: swapping the car is not a reason to make somebody re-enter the
 * order around it.
 *
 * **A different fuel clears the litres and steps back.** «40 л» typed for a petrol sedan is not a
 * quantity anybody confirmed for a diesel van, and holding money against it is worse than asking
 * again. A changed *tank size* clears nothing — «до полного» simply re-reads it, and a figure that
 * still fits is still what was asked for.
 *
 * **Extras that no longer match are dropped.** One выезд carries one fuel; the backend answers a
 * mixed one with «В один выезд попадают машины на одном топливе» and a 400. An extra whose own
 * fuel is unknown is dropped too — a draft persisted before that field existed cannot be
 * re-checked, and an unverifiable extra is precisely the refusal this prevents.
 *
 * **The promoted car leaves the extras.** The picker lists the whole garage with no exclusions, so
 * promoting an extra to primary is an ordinary thing to do. Leaving it in both places sends the
 * same car twice, which is a duplicate-car 400, and counts it twice against the city's cap.
 */
export function withCar(draft: OrderDraft, car: DraftCar): OrderDraft {
  const fuelChanged = draft.fuelTypeCode !== null && draft.fuelTypeCode !== car.fuelTypeCode;

  const updated: OrderDraft = {
    ...draft,
    // Другая машина — другая сумма и другой ключ идемпотентности (см. `applyDraftPatch`).
    externalId: freshDraftKey(),
    carId: car.id,
    carLabel: car.label,
    carBrand: car.brand,
    fuelTypeCode: car.fuelTypeCode,
    tankVolumeLiters: car.tankVolumeLiters,
    extraCars: draft.extraCars.filter(
      (extra) => extra.carId !== car.id && extra.fuelTypeCode === car.fuelTypeCode,
    ),
  };

  if (!fuelChanged) return updated;
  return { ...updated, liters: '', step: earlierOf(updated.step, 'fuel') };
}

/**
 * Adds one more car to the same выезд.
 *
 * A car already on the draft is ignored rather than duplicated, and so is one past the city's cap.
 * The backend refuses both; silently accepting either here turns a mis-tap into a rejected order.
 */
export function withExtraCar(draft: OrderDraft, car: ExtraCar): OrderDraft {
  if (draft.carId === car.carId) return draft;
  if (draft.extraCars.some((extra) => extra.carId === car.carId)) return draft;
  if (!canAddAnotherCar(draft)) return draft;
  return { ...draft, extraCars: [...draft.extraCars, car], externalId: freshDraftKey() };
}

export function withoutExtraCar(draft: OrderDraft, carId: string): OrderDraft {
  const extraCars = draft.extraCars.filter((extra) => extra.carId !== carId);
  if (extraCars.length === draft.extraCars.length) return draft;
  return { ...draft, extraCars, externalId: freshDraftKey() };
}

/**
 * Whether this car can join the выезд that is already being built.
 *
 * The fuel check is the one that matters and the one a list must apply before showing anything:
 * offering a diesel van next to a petrol sedan produces a tap that ends in a 400 rather than a
 * greyed row that explains itself.
 */
export function canJoinTrip(draft: OrderDraft, fuelTypeCode: string): boolean {
  return draft.fuelTypeCode === null || draft.fuelTypeCode === fuelTypeCode;
}
